<!--
  Forbrukerrådet - Kommunetesten
  @link https://gitlab01.copyleft.no/FBR/kommunetesten-new
  @author Anders Evenrud <anders.evenrud@copyleft.no>
-->
<template>
  <div class="statistics">
    <slot />

    <ul>
      <li v-for="(group, index) in items" :key="index" class="statistics__group">
        <div class="statistics__item" :class="itemClass(index)">
          <div
            class="statistics__item__label"
            role="button"
            tabindex="0"
            @click.prevent="onGroupToggle($event, index)"
            @keypress.enter="onGroupToggle($event, index)"
            @keypress.prevent.space="onGroupToggle($event, index)"
          >
            <font-awesome-icon :icon="isActive(index) ? 'minus' : 'plus'" />
            <span>{{ group.label }}</span>
          </div>
          <template>
            <Progressbar :progress="group.score" />
            <div class="statistics__item__score">
              <span>{{ scoreLabel(group.score, group.measured) }}</span>
            </div>
          </template>
        </div>

        <ul class="statistics__subitems">
          <li v-for="(item, index) in group.items" :key="index">
            <div class="statistics__subitem">
              <div class="statistics__subitem__label">
                <span>{{ item.label }}</span>
              </div>

              <Progressbar :progress="item.score" />

              <div class="statistics__subitem__score">
                <span>{{ scoreLabel(item.score, group.measured) }}</span>
              </div>
            </div>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
@import "../assets/variables";

.statistics {
  border: $statistics-border;

  &__item {
    background-color: $statistics-header-background;

    &__label {
      cursor: pointer;
      color: $statistics-header-font-color;

      span {
        padding-left: 1em;
      }
    }

    &--collapsed {
      margin-bottom: 1px;

      & + ul {
        max-height: 0;
      }
    }

    .progressbar {
      height: 0.66em;
    }
  }

  &__subitems {
    overflow: hidden;
    transition: all 0.25s ease-in-out;
    max-height: 10em;
    overflow: hidden;

    & > li:not(:last-child) {
      .statistics__subitem {
        border-bottom: $statistics-border;
      }
    }
  }

  &__subitem {
    &__label,
    &__score {
      font-size: 88%;
    }

    .progressbar {
      height: 0.5em;
    }
  }

  &__item,
  &__subitem {
    padding: 1em;
    display: flex;
    align-items: center;
    position: relative;

    &__label {
      flex: 0 1 36%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: normal;
    }

    .progressbar {
      flex: 1 1;
      margin-left: 1em;
      margin-right: 1em;
    }

    &__score {
      white-space: nowrap;
      text-align: right;
      width: 8rem;
    }
  }
}

ul {
  margin: 0;
  padding: 0;
  list-style-type: none;
}

li {
  margin: 0;
  padding: 0;
}

@media (max-width: $responsive-break) {
  .statistics {
    &__subitems {
      max-height: 16em;
    }

    &__item,
    &__subitem {
      padding-bottom: 2em;

      .progressbar {
        position: absolute;
        bottom: 1em;
        left: 0;
        right: 0;
      }

      &__label {
        flex: 1 1 50%;
      }

      &__score {
        flex: 1 1;
        width: auto;
      }
    }
  }
}
</style>

<script>
import Progressbar from './Progressbar.vue'

export default {
  props: {
    items: Array
  },

  components: {
    Progressbar
  },

  data () {
    return {
      active: this.items.map(() => false)
    }
  },

  methods: {
    isActive (index) {
      return this.active[index]
    },

    onGroupToggle (ev, index) {
      this.$set(this.active, index, !this.active[index])
    },

    scoreLabel (score, measured) {
      return isNaN(score)
        ? 'Ikke testet'
        : `${score} / 100`
    },

    itemClass (index) {
      return 'statistics__item' + (this.isActive(index)
        ? '--expanded'
        : '--collapsed')
    }
  }
}
</script>
